// src/pages/settings/PdlForm.tsx — formulaire de création/édition d'un PDL (électricité)

import { useState } from "react";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import type { PdlRecord } from "../../api/metersApi";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { SiteSelect } from "./SiteSelect";

export function PdlForm({ initial, sites, clients, existingPdls, setModal, onSave, onClose, canEditSge = false, canEditEngieSource = false }: {
  initial?: PdlRecord; sites: SiteRecord[]; clients: ClientRecord[]; existingPdls: PdlRecord[];
  setModal: (m: ConfirmModalProps | null) => void;
  onSave: (pdl: PdlRecord) => Promise<void>; onClose: () => void; canEditSge?: boolean;
  /** "engie" n'est jamais un choix normal du select Source (seule porte d'entrée : EngieImportModal)
   * — verrouille le select si la source courante est déjà "engie" et que l'appelant n'est pas admin. */
  canEditEngieSource?: boolean;
}) {
  const [id,                 setId]                 = useState(initial?.id ?? "");
  const [segment,            setSegment]            = useState(initial?.segment ?? "");
  const [puissanceSouscrite, setPuissanceSouscrite] = useState(initial?.puissanceSouscrite ?? "");
  const [codePostal,         setCodePostal]         = useState(initial?.codePostal ?? "");
  const [siteId,             setSiteId]             = useState(initial?.siteId ?? "");
  const [sge,                setSge]                = useState(initial?.sge ?? false);
  const [source,             setSource]             = useState(initial?.source ?? "");
  const [label,              setLabel]              = useState(initial?.label ?? "");
  const [saving,             setSaving]             = useState(false);
  const [err,                setErr]                = useState<string|null>(null);
  const isEngieLocked = source === "engie" && !canEditEngieSource;

  const doSave = async (trimmedId: string) => {
    setSaving(true);
    try {
      await onSave({ id: trimmedId, segment, puissanceSouscrite, codePostal, siteId, sge, source, label, createdAt: "", updatedAt: "" });
      onClose();
    } catch(e) { setErr((e as Error).message); setSaving(false); }
  };

  const handleSave = async () => {
    if (!id.trim())  { setErr("Le numéro PDL est obligatoire."); return; }
    const trimmedId = id.trim();
    const existing = !initial ? existingPdls.find(p=>p.id===trimmedId) : undefined;
    if (existing) {
      const siteName = sites.find(s=>s.id===existing.siteId)?.name ?? "Sans site";
      setModal({
        title: "PDL déjà existant",
        message: `Un PDL "${trimmedId}" existe déjà (site rattaché : ${siteName}). L'enregistrer écrasera ses données actuelles. Continuer ?`,
        confirmLabel: "Écraser", danger: true,
        onCancel: () => setModal(null),
        onConfirm: () => doSave(trimmedId),
      });
      return;
    }
    await doSave(trimmedId);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
        {initial
          ? <div className="mb-16" style={{padding:"8px 12px",background:"#f8f8f8",borderRadius:6,fontSize:13}}>
              PDL : <strong>{initial.id}</strong>
            </div>
          : <div className="mb-16">
              <label className="label">Numéro PDL *</label>
              <input value={id} onChange={e=>{setId(e.target.value);setErr(null);}} className="field" placeholder="ex : 12345678901234"/>
            </div>
        }
        <div className="mb-16">
          <label className="label">Source</label>
          <select value={source} onChange={e=>setSource(e.target.value)} className="field" disabled={isEngieLocked}>
            <option value="">— Aucune —</option>
            <option value="enedis">Enedis</option>
            <option value="deepki">Deepki</option>
            {source === "engie" && <option value="engie">Engie</option>}
          </select>
          {isEngieLocked && (
            <span style={{fontSize:11,color:"var(--text-faint)",display:"block",marginTop:4}}>
              Alimenté par un import Engie — modifiable uniquement par un administrateur.
            </span>
          )}
        </div>
        {source === "enedis" && (
          <div className="mb-16">
            <label className="label" style={{marginBottom:6}}>SGE (Enedis)</label>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:canEditSge?"pointer":"default",fontSize:13,color:canEditSge?"var(--text-primary)":"var(--text-secondary)",fontFamily:"var(--ff-title)",opacity:canEditSge?1:0.7}}>
              <input type="checkbox" checked={sge} onChange={e=>canEditSge&&setSge(e.target.checked)}
                disabled={!canEditSge}
                style={{width:16,height:16,cursor:canEditSge?"pointer":"not-allowed",accentColor:"var(--green-dim)"}}/>
              Données accessibles via SGE
              {!sge && <span style={{color:"#c0392b",fontSize:12}}>🚫 non accessible</span>}
              {!canEditSge && <span style={{fontSize:11,color:"var(--text-secondary)"}}>— réservé aux administrateurs</span>}
            </label>
          </div>
        )}
        <div className="mb-16">
          <label className="label">Site</label>
          <SiteSelect value={siteId} onChange={setSiteId} sites={sites} clients={clients} placeholder="— Sans site —"/>
        </div>
        <div className="mb-16">
          <label className="label">Libellé</label>
          <input value={label} onChange={e=>setLabel(e.target.value)} className="field" placeholder="ex : Bâtiment A"/>
        </div>
        <div className="mb-16" style={{display:"flex",gap:12}}>
          <div style={{flex:1}}>
            <label className="label">Code postal</label>
            <input value={codePostal} onChange={e=>setCodePostal(e.target.value)} className="field" placeholder="ex : 75001"/>
          </div>
          <div style={{flex:1}}>
            <label className="label">Segment</label>
            <input value={segment} onChange={e=>setSegment(e.target.value)} className="field" placeholder="ex : C5"/>
          </div>
          <div style={{flex:1}}>
            <label className="label">Puissance souscrite</label>
            <input value={puissanceSouscrite} onChange={e=>setPuissanceSouscrite(e.target.value)} className="field" placeholder="ex : 36 kVA"/>
          </div>
        </div>
      </div>
      <div style={{padding:"16px 24px",borderTop:"1px solid #f0f0f0",background:"#fff",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
        {err && <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:13,color:"var(--red)"}}>⚠ {err}</div>}
        <div className="flex gap-10" style={{justifyContent:"flex-end"}}>
          <button onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-black btn-lg">{saving?"Enregistrement…":"Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}
